import * as Yup from "yup";
import { fillTemplate } from "../../../../utils/renderTemplate";

// Objetivos guardados antes de ampliar el listado de verbos usaban estos valores.
const LEGACY_ACTION_MAP = { reduce: "Reducir", increase: "Aumentar" };

function normalizeAction(action) {
  if (!action) return null;
  return LEGACY_ACTION_MAP[action] || action;
}

// <input type="date"> solo acepta "YYYY-MM-DD"; el backend devuelve fecha ISO completa.
function toDateInputValue(date) {
  if (!date) return null;
  const iso = typeof date === "string" ? date : new Date(date).toISOString();
  return iso.slice(0, 10);
}

export function initialValues(environmentalObjetives, year, aspect) {
  return {
    aspect: environmentalObjetives?.aspect || aspect,
    // Año de reporte = año meta: debe ser posterior al año base (año actual).
    year: environmentalObjetives?.year || (year > new Date().getFullYear() ? year : new Date().getFullYear() + 1),
    action: normalizeAction(environmentalObjetives?.action),
    source: environmentalObjetives?.source || null,
    reductionTons: environmentalObjetives?.reductionTons || null,
    periodStart: toDateInputValue(environmentalObjetives?.periodStart),
    periodEnd: toDateInputValue(environmentalObjetives?.periodEnd),
    measure: environmentalObjetives?.measure || null,
    impact: environmentalObjetives?.impact || null,
    investment: environmentalObjetives?.investment || null,
    // Al editar un objetivo ya guardado sin KPI (kpiCode null), mostrar el
    // sentinel "SINKPI" para que el select abra en modo "Sin KPI" (indicador
    // propio) en vez de vacío. Un objetivo NUEVO arranca en null (todavía no
    // se eligió nada).
    kpiCode: environmentalObjetives ? (environmentalObjetives.kpiCode || "SINKPI") : null,
    // Año de línea base: antes se congelaba a mano en onSubmit leyendo la
    // config vigente, ahora es un campo real que el usuario puede elegir (ver
    // EnvironmentalObjetivesForm.js). Al editar un objetivo ya guardado se
    // respeta el año que tiene guardado; si no tiene (objetivo viejo), queda
    // null y el useEffect de KPI lo autocompleta con la config vigente.
    baselineYear: environmentalObjetives?.baselineYear ?? null,
    baseMedicion: environmentalObjetives?.baseMedicion || "ambas",
    relevantConfirmed: environmentalObjetives?.relevantConfirmed || false,
    prodGrowth: environmentalObjetives?.prodGrowth ?? 4,
    responsible: environmentalObjetives?.responsible || null,
    frequency: environmentalObjetives?.frequency || "Trimestral",
    active: environmentalObjetives?.active || null,
  };
}

export function validationSchema(sumImpact, t, baselineYear) {
  const min = (n) => fillTemplate(t("validation_min_template"), { n });
  const max = (n) => fillTemplate(t("validation_max_template"), { n });
  // El año base real es el de la Línea Base del KPI vinculado (☰ → Datos →
  // Línea Base), no el año calendario actual. Mientras todavía no se eligió
  // un KPI (o no tiene línea base configurada), se usa el año actual como
  // referencia provisoria para no bloquear el formulario sin motivo.
  const effectiveBaseYear = baselineYear ?? new Date().getFullYear();

  return Yup.object({
     aspect: Yup.string().required(t("validation_select_aspect")),
    year: Yup.number()
      .min(effectiveBaseYear + 1, t("validation_year_after_base"))
      .max(effectiveBaseYear + 10, t("validation_year_max_horizon"))
      .required(t("validation_select_year")),
    action: Yup.string().required(t("select_action")),
    source: Yup.string().required(t("validation_enter_source")),
    reductionTons: Yup.number()
      .min(1, min(1))
      .max(100, max(100))
      .required(t("validation_reduction_required")),
    periodStart: Yup.date().required(t("validation_select_period_start")),
    periodEnd: Yup.date()
      .test("after-start", t("validation_period_end_after_start"), function (value) {
        const { periodStart } = this.parent;
        if (!value || !periodStart) return true; // el required() de abajo cubre los vacíos
        return new Date(value) > new Date(periodStart);
      })
      .required(t("validation_select_period_end")),
    measure: Yup.string().required(t("validation_enter_measure")),
    impact: Yup.number()
      .min(0.5, min(0.5))
      .max(100, max(100))
      .required(t("validation_enter_impact"))
      .test(
        "impact-sum",
        t("validation_impact_sum"),
        function (value) {
          return sumImpact + Number(value || 0) <= 100;
        }
      ),
    investment: Yup.string().required(t("validation_enter_investment")),
    kpiCode: Yup.string().required(t("validation_select_kpi")),
    baseMedicion: Yup.string()
      .oneOf(["absoluta", "normalizada", "ambas"])
      .required(t("validation_select_base_medicion")),
    prodGrowth: Yup.number().min(0, min(0)).max(50, max(50)),
    responsible: Yup.string().required(t("validation_enter_responsible")),
    frequency: Yup.string()
      .oneOf(["Mensual", "Trimestral", "Semestral", "Anual"])
      .required(t("validation_select_frequency")),
    relevantConfirmed: Yup.boolean().oneOf([true], t("validation_confirm_relevance")),
  });
}
